import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X } from "lucide-react";
import { FormField, useFieldIds } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";

export interface ComboboxOption {
  value: string;
  label: string;
  /** El renglón pequeño bajo la etiqueta: la categoría, el precio, el estado. */
  hint?: string;
  /**
   * Se enseña pero no se puede elegir, y con el motivo escrito. Un
   * elemento que desaparece de la lista deja a quien lo busca creyendo
   * que no existe; uno atenuado con su explicación no.
   */
  disabled?: boolean;
  disabledReason?: string;
}

export interface ComboboxProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  options: readonly ComboboxOption[];
  /** El valor elegido, o `""`. Controlado, siempre. */
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Qué se lee cuando el filtro no deja nada. */
  emptyLabel?: string;
  id?: string;
  className?: string;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Un desplegable que se puede BUSCAR.
 *
 * ── Por qué no un `<select>` ────────────────────────────────────────
 * El sistema usa `<select>` nativo siempre que puede, y con veinte
 * opciones es la elección correcta. Con doscientas deja de serlo: un
 * catálogo entero en una lista que solo se recorre con la rueda es
 * exactamente lo que Anahí pidió evitar. El nativo permite escribir para
 * saltar, pero solo por la PRIMERA letra y solo si se teclea rápido, así
 * que "Sexto Elemento" no se encuentra escribiendo "elemento".
 *
 * ── Y por qué no un `<input list>` con `<datalist>` ─────────────────
 * Porque un `<datalist>` casa por la ETIQUETA y devuelve la etiqueta,
 * no el id. Aquí lo que hay que guardar es el id del producto, así que
 * habría que traducir el texto de vuelta a un id — y dos productos con
 * el mismo nombre, que existen, harían esa traducción imposible.
 *
 * ── Lo que implementa, y con qué patrón ─────────────────────────────
 * El patrón `combobox` de ARIA: un `<input>` con `role="combobox"`,
 * `aria-expanded`, `aria-controls` y `aria-activedescendant` apuntando a
 * la opción resaltada. Eso da lo que hace falta:
 *
 *  · ↑ ↓ recorren, Enter elige, Escape cierra sin elegir.
 *  · El foco NUNCA sale del `<input>`, así que se puede seguir
 *    escribiendo mientras se navega.
 *  · Un lector de pantalla anuncia la opción resaltada al moverse,
 *    porque `aria-activedescendant` es lo que ese patrón usa en vez de
 *    mover el foco.
 *
 * Es genérico a propósito: no sabe de productos ni de catálogos.
 * ═══════════════════════════════════════════════════════════════════
 */
export function Combobox({
  label,
  hint,
  error,
  required,
  disabled,
  options,
  value,
  onChange,
  placeholder,
  emptyLabel,
  id,
  className,
}: ComboboxProps) {
  const { t } = useTranslation();
  const listId = useId();
  const { controlId, hintId, errorId, describedBy, invalid } = useFieldIds(id, {
    hasHint: Boolean(hint),
    hasError: Boolean(error),
  });

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const selected = options.find((option) => option.value === value);

  /**
   * El filtro compara SIN acentos y sin mayúsculas, y por
   * subcadena —no por prefijo—.
   *
   * Sin lo primero, "oxigenacion" no encuentra "Oxigenación", que es
   * justo lo que alguien teclea con prisa. Sin lo segundo, "elemento" no
   * encuentra "Sexto Elemento", que es el nombre por el que el producto
   * se conoce.
   */
  const filtered = useMemo(() => {
    const needle = query
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim();
    if (!needle) return options;
    return options.filter((option) =>
      `${option.label} ${option.hint ?? ""}`
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .includes(needle),
    );
  }, [options, query]);

  // Cerrar al pulsar fuera. Sin esto, la lista se queda abierta encima
  // del campo siguiente y tapa lo que se está escribiendo.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: PointerEvent): void {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function choose(option: ComboboxOption): void {
    if (option.disabled) return;
    onChange(option.value);
    setQuery("");
    setOpen(false);
  }

  function move(direction: 1 | -1): void {
    if (filtered.length === 0) return;
    setHighlight((current) => {
      const next = (current + direction + filtered.length) % filtered.length;
      return next;
    });
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    /*
     * `stopPropagation` en las teclas que este control usa: el formulario
     * del producto captura Enter para AVANZAR DE PASO, así que sin esto
     * elegir un producto de la lista saltaría al paso siguiente.
     */
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!open) setOpen(true);
        else move(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        move(-1);
        break;
      case "Enter": {
        if (!open) return;
        event.preventDefault();
        event.stopPropagation();
        const option = filtered[highlight];
        if (option) choose(option);
        break;
      }
      case "Escape":
        if (!open) return;
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        break;
      default:
        break;
    }
  }

  return (
    <FormField
      label={label}
      hint={hint}
      error={error}
      required={required}
      controlId={controlId}
      hintId={hintId}
      errorId={errorId}
      className={className}
    >
      <div ref={boxRef} className="relative">
        <div className="relative">
          <input
            id={controlId}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={open ? `${listId}-${highlight}` : undefined}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            disabled={disabled}
            autoComplete="off"
            /*
              Cuando hay algo elegido y no se está buscando, el campo
              enseña el NOMBRE de lo elegido. Es lo que hace que la fila
              se lea como "Detox Elemental × 2" y no como un id de
              veinticuatro caracteres que no significa nada.
            */
            value={open ? query : (selected?.label ?? "")}
            placeholder={placeholder}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setHighlight(0);
              if (!open) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            className={cn(
              "block w-full rounded-2xl border bg-ds-surface px-4 py-3 pr-16 text-sm text-ds-ink",
              "outline-none transition placeholder:text-ds-ink-4",
              "focus:border-ds-lilac focus:ring-2 focus:ring-ds-lavender-bg-2/50",
              "disabled:cursor-not-allowed disabled:bg-ds-cream-2 disabled:opacity-60",
              error
                ? "border-ds-terracotta focus:border-ds-terracotta focus:ring-ds-terracotta/20"
                : "border-ds-border",
            )}
          />

          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1">
            {selected && !disabled && (
              <button
                type="button"
                aria-label={t("Quitar la elección")}
                onClick={() => {
                  onChange("");
                  setQuery("");
                }}
                className="pointer-events-auto rounded-full p-1 text-ds-ink-3 hover:bg-ds-cream-2"
              >
                <X aria-hidden="true" className="h-3.5 w-3.5" />
              </button>
            )}
            <ChevronDown aria-hidden="true" className="h-4 w-4 text-ds-ink-3" />
          </div>
        </div>

        {open && (
          <ul
            id={listId}
            role="listbox"
            className={cn(
              "absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-2xl border border-ds-border",
              "bg-ds-surface p-1 shadow-glass",
            )}
          >
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-[12px] text-ds-ink-3">
                {emptyLabel ?? t("Nada coincide con lo que escribiste.")}
              </li>
            )}

            {filtered.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled || undefined}
                /*
                  `onPointerDown` y no `onClick`: el clic llega DESPUÉS
                  del `blur`, y para entonces el "cerrar al pulsar fuera"
                  ya habría desmontado esta lista. Elegir con el ratón
                  no funcionaría y no habría nada que lo explicara.
                */
                onPointerDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
                onMouseEnter={() => setHighlight(index)}
                className={cn(
                  "grid cursor-pointer gap-0.5 rounded-xl px-3 py-2 transition",
                  index === highlight && "bg-ds-lavender-bg-2/40",
                  option.disabled && "cursor-not-allowed opacity-60",
                )}
              >
                <span className="text-[13px] leading-snug text-ds-ink">{option.label}</span>
                {(option.hint || (option.disabled && option.disabledReason)) && (
                  <span className="text-[11px] leading-relaxed text-ds-ink-3">
                    {option.disabled && option.disabledReason
                      ? option.disabledReason
                      : option.hint}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </FormField>
  );
}
